import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { SelectType } from '@components/forms/offline-sales/select-type';
import { IconDownload } from '@components/icons';
import { SummaryFormModalContent, SummarySuccessModalContent } from '@components/modals';
import { SummaryFormMultiModalContent } from '@components/modals/summary-form-multi-modal';
import { TicketDownloadModal } from '@components/ticket-download-modal';
import { STEP_INFO, STEP_SELECT_TYPE, STEP_SUCCESS } from '@constants/process-steps';
import { Addon } from '@domain/addon';
import { Admission } from '@domain/admission';
import { Event } from '@domain/event';
import { useCheckTicketDownload } from '@hooks/bookings/use-check-ticket-download';
import { AddGuestResponse } from '@hooks/use-add-guest/use-add-guest';
import { getBookingLinkUrl } from '@utils/create-booking-link';

import {
  OfflineSalesForm,
  ProcessOfflineSalesStep,
  SaleType,
  SINGLE_OFFLINE_SELL,
} from '../process-offline-sales.type';

export const ProcessStepContent = ({
  step,
  offer,
  event,
  saleType,
  quantity,
  availableAddons,
  addGuestData,
  selectedAddonIds,
  email,
  isSubmitting,
  closeModal,
  handleCloseModal,
  onSubmit,
  setStep,
}: {
  step: ProcessOfflineSalesStep;
  offer?: Admission;
  event?: Event;
  saleType: SaleType;
  quantity: number;
  availableAddons?: Addon[];
  addGuestData?: AddGuestResponse;
  selectedAddonIds?: string[];
  email?: string;
  isSubmitting?: boolean;
  handleCloseModal: () => void;
  closeModal: () => void;
  setStep: (step: ProcessOfflineSalesStep) => void;
  onSubmit: (e: OfflineSalesForm) => void;
}) => {
  const { t } = useTranslation();
  const methods = useFormContext<OfflineSalesForm>();
  const { handleSubmit } = methods;
  const { checkAndDownload, downloadModalProps } = useCheckTicketDownload();

  switch (step) {
    case STEP_SELECT_TYPE:
      return <SelectType onContinue={() => setStep(STEP_INFO)} offer={offer} />;
    case STEP_INFO:
      return (
        offer &&
        event &&
        (saleType === SINGLE_OFFLINE_SELL ? (
          <SummaryFormModalContent
            event={event}
            offer={offer}
            addons={availableAddons ?? []}
            quantity={quantity}
            selectedAddons={selectedAddonIds}
            showNominativeFields
            onContinue={handleSubmit(onSubmit)}
            handleClose={handleCloseModal}
            title={t('offers:fillInfoOfflineSalesTitle')}
            mainActionText={t('offers:confirmSaleButtonText')}
            isSubmitLoading={isSubmitting}
          />
        ) : (
          <SummaryFormMultiModalContent
            event={event}
            offer={offer}
            addons={availableAddons ?? []}
            quantity={quantity}
            selectedAddons={selectedAddonIds}
            showNominativeFields
            onContinue={handleSubmit(onSubmit)}
            handleClose={handleCloseModal}
            title={t('offers:fillInfoOfflineSalesTitle')}
            mainActionText={t('offers:confirmSaleButtonText')}
            isSubmitLoading={isSubmitting}
          />
        ))
      );
    case STEP_SUCCESS:
      return (
        offer &&
        addGuestData && (
          <>
            <SummarySuccessModalContent
              offer={offer}
              addons={availableAddons ?? []}
              quantity={quantity}
              bookingLegacyId={addGuestData?.legacyId}
              onClickMainAction={() => setStep(STEP_SELECT_TYPE)}
              onClickSecondaryAction={() => checkAndDownload(addGuestData.token, getBookingLinkUrl(addGuestData.token))}
              secondaryActionIcon={IconDownload}
              secondaryActionText={t('offers:downloadPdf')}
              onClickClose={closeModal}
              title={t('offers:processOfflineSalesSuccessTitle')}
              headerDescription={
                email ? (
                  <span className='text-secondary text-body-s'>
                    <Trans
                      i18nKey='offers:processEmailBookingSuccessDescription'
                      components={{ b: <b /> }}
                      values={{ email, bookingLegacyId: addGuestData.legacyId }}
                    />
                  </span>
                ) : undefined
              }
              mainActionText={t('offers:buttonSaleMore')}
            />
            <TicketDownloadModal {...downloadModalProps} />
          </>
        )
      );
    default:
      return null;
  }
};
